import { Headline, Icon, Pagination, Text, TextLink, TextList, TextListItem } from '@oneaudi/unified-web-components';
import { useCallback, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import {
	DialogWrapper,
	MobileContainer,
	StyledCard,
	StyledDivider,
	StyledSlider,
} from '~/app/pages/configurator/components/comparison-dialog/components/styles/comparison-dialog-styles';
import { DialogItemActionButton } from '~/app/pages/configurator/components/more-information-dialog/components/dialog-item-action-button';
import { Dialog } from '~/components/dialog';
import { useFootnotes } from '~/components/footnotes';
import { MONTHLY_RATE_DIALOG_REMARK } from '~/components/footnotes/constants';
import { Prices } from '~/components/prices';
import { useSlider } from '~/components/slider-panel/hooks/use-slider';
import { useSwipeHandlers } from '~/components/slider-panel/hooks/use-swipe-handlers';
import { RenderHtml } from '~/components/text';
import { SelectionCardItem } from '~/graphql/types';
import { RegisterOrUpdateRateFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/monthly-rate-footnote';

export function CompareOptionsDialog({ items }: { items: SelectionCardItem[] }) {
	const { i18n } = useI18n();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Dialog
			classNameDialog="comparison-dialog"
			triggerElement={
				<TextLink variant={'standalone'} size={'sm'} onClick={() => setIsOpen(true)}>
					{i18n('ngc.dialogs.comparison.trigger')}
				</TextLink>
			}
			onCloseButtonClick={() => setIsOpen(false)}
			open={isOpen}>
			<CompareOptionsDialogInner items={items} setIsOpen={setIsOpen} />
		</Dialog>
	);
}

function CompareOptionsDialogInner({
	items,
	setIsOpen,
}: {
	setIsOpen: (isOpen: boolean) => void;
	items: SelectionCardItem[];
}) {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();

	if (config.enableFinancingServices && !config.hideMonthlyRates && config.showFinancingDisclaimerInDialogs) {
		RegisterOrUpdateRateFootnoteForDialog();
	}

	return (
		<>
			<span>
				<Headline tag="h2" className="flex gap-1" variant={{ type: 'headline', order: '4', weight: 'normal' }}>
					<Icon className="flex items-center justify-center" name="compare" size="s" />
					{i18n('ngc.dialogs.comparison.title')}
				</Headline>
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					{i18n('ngc.dialogs.comparison.subtitle')}
				</Text>
			</span>
			<MobileComparison setIsOpen={setIsOpen} items={items} />

			<DialogWrapper $itemCount={items.length}>
				<div className="header">
					{items.map((item) => (
						<Headline
							className="header__title"
							key={item.key}
							tag="span"
							variant={{ type: 'headline', order: '4', weight: 'bold' }}>
							{item.name}
						</Headline>
					))}

					{items.map((item) => (
						<ConfigContainer setIsOpen={setIsOpen} key={item.key} item={item} />
					))}
				</div>
				{items.map((item) => (
					<span className="content" key={item.key}>
						{item.imgUrl && <img src={item.imgUrl || ''} alt={item.name || ''} />}
					</span>
				))}
				{items.map((item) => (
					<Text
						key={item.key}
						className="content__teaser-text"
						variant={{ type: 'copy', order: '2', style: 'normal' }}>
						<RenderHtml html={item.description} />
					</Text>
				))}

				{items.map((item) => (
					<FeatureInformation key={item.key} item={item} />
				))}
			</DialogWrapper>
		</>
	);
}

const ConfigContainer = ({ item, setIsOpen }: { setIsOpen: (isOpen: boolean) => void; item: SelectionCardItem }) => {
	const vatConfig = useVATConfig();
	const { getReference } = useFootnotes();

	const onAddToConfiguration = useCallback(() => {
		setIsOpen(false);
		item.onSelect();
	}, [item, setIsOpen]);

	return (
		<div className="header__content" key={item.key}>
			<Prices
				totalPrice={item.price?.totalPrice}
				monthlyRateDialog={item.price?.monthlyRate}
				monthlyRateDialogReference={getReference(MONTHLY_RATE_DIALOG_REMARK)}
				vatConfig={vatConfig.feature}
				displayMode="rows"
			/>

			{item.status && <DialogItemActionButton status={item.status} onAddToConfiguration={onAddToConfiguration} />}
		</div>
	);
};

const FeatureInformation = ({ item }: { item: SelectionCardItem }) => {
	const { i18n } = useI18n();

	return (
		<div className="content">
			{item.content && item.content?.length > 0 && (
				<>
					<StyledDivider />
					<Text className="features-title" variant={{ type: 'copy', order: '1', style: 'bold' }}>
						{i18n('ngc.dialogs.features')}
					</Text>
					<TextList variant={'bullet'}>
						{item.content.map((feature, index) => (
							<TextListItem key={index}>
								<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>{feature.name}</Text>
							</TextListItem>
						))}
					</TextList>
				</>
			)}
		</div>
	);
};

const MobileComparison = ({
	setIsOpen,
	items,
}: {
	setIsOpen: (isOpen: boolean) => void;
	items: SelectionCardItem[];
}) => {
	const vatConfig = useVATConfig();
	const cardRef = useRef<HTMLDivElement>(null);

	const CARD_GAP = 36; // Gap for cards wrapper (px)

	const cardWidth = cardRef.current ? cardRef.current.offsetWidth + CARD_GAP : 0;

	const { activeItem, setActiveItem, slideBack, slideNext } = useSlider(true, cardWidth, 0, items.length);

	const onAddToConfiguration = useCallback(() => {
		setIsOpen(false);
		items[activeItem].onSelect();
	}, [activeItem, items, setIsOpen]);

	return (
		<>
			<MobileContainer>
				<div className="header">
					<StyledSlider gap={CARD_GAP} {...useSwipeHandlers({ slideBack, slideNext })}>
						<div
							style={{ transform: `translateX(-${activeItem * cardWidth}px)` }}
							className="cards-wrapper">
							{items?.map((item, index) => (
								<StyledCard
									onClick={() => setActiveItem(index)}
									isShown={activeItem === index}
									ref={cardRef}
									key={index}>
									<Headline tag="h4" variant={{ type: 'headline', order: '4', weight: 'normal' }}>
										{item.name}
									</Headline>
									<Prices
										totalPrice={item.price?.totalPrice}
										monthlyRateDialog={item.price?.monthlyRate}
										vatConfig={vatConfig.feature}
										displayMode="rows"
									/>
								</StyledCard>
							))}
						</div>
					</StyledSlider>
					<Pagination
						current={activeItem + 1}
						itemCount={items.length}
						decrementElement={{
							'aria-label': 'Decrease',
							onClick: () => setActiveItem(activeItem - 1),
						}}
						incrementElement={{
							'aria-label': 'Increase',
							onClick: () => setActiveItem(activeItem + 1),
						}}
						variant="compact"
					/>

					{items[activeItem].status && (
						<DialogItemActionButton
							status={items[activeItem].status}
							onAddToConfiguration={onAddToConfiguration}
						/>
					)}
				</div>
				{items[activeItem].imgUrl && (
					<img
						src={items[activeItem].imgUrl || ''}
						className="mt-2 h-full max-h-64 w-full object-contain"
						alt=""
						loading="lazy"
					/>
				)}

				<Text className="teaserText" variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={items[activeItem].description} />
				</Text>

				<FeatureInformation item={items[activeItem]} />
			</MobileContainer>
		</>
	);
};
